import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Email admin API service
 * Endpoints inferred from Postman collection 'Email' section.
 * Base path adjusted to email templates endpoint: /api/email-templates
 * Email template record shape (expected):
 * {
 *   id: string|number,
 *   templateKey: string,             // unique logical key (e.g. 'welcome')
 *   subject: string,                 // may include handlebars style tokens {{firstName}}
 *   bodyHtml: string,                // full HTML markup
 *   bodyText: string,                // plaintext alternative
 *   description: string,             // admin description
 *   isActive: boolean,               // whether template is enabled for sending
 *   createdAt?: string, updatedAt?: string
 * }
 * Create/Update payload MUST match:
 * {
 *   templateKey, subject, bodyHtml, bodyText, description, isActive
 * }
 */
export const EmailApi = {
  /** List emails (optional pagination params) */
  getEmails: async ({ page = 0, size = 20 } = {}) => {
    const url = new URL(`${DATA_SERVICE_BASE_URL}/api/email-templates`);
    url.searchParams.set('page', page);
    url.searchParams.set('size', size);
    return fetchWithAuth(url.toString(), { method: 'GET' });
  },
  /** Fetch single email by id */
  getEmail: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/email-templates/${id}`, { method: 'GET' });
  },
  /** Create new email draft */
  createEmail: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/email-templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  },
  /** Update existing email */
  updateEmail: async (id, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/email-templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  },
  /**
   * Send email(s) using template key.
   * Backend expects POST /api/email-templates/send with body { templateKey, toEmail }
   * For multiple recipients we will iterate sequentially.
   */
  sendEmail: async (templateKey, toEmail, fields = {}) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/email-templates/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateKey, toEmail, fields })
    });
  },
  /**
   * Send user feedback directly to corporate email.
   * Backend endpoint: POST /api/email-templates/feedback
   * Requires authenticated user. User info is auto-populated on backend.
   */
  sendFeedback: async (subject, message, sourcePage, environment) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/email-templates/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, message, sourcePage, environment })
    });
  },
  /** Delete email (if supported) */
  deleteEmail: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/email-templates/${id}`, { method: 'DELETE' });
  }
};
